import { useMemo, useState } from 'react';
import { useProjetos } from '../hooks/useProjetos';
import { useHistorico } from '../hooks/useHistorico';
import { cores } from '../styles/cores';
import { Campo, EstadoVazio, Input, Select, Spinner } from '../components/UI';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatarData(ts) {
  if (!ts) return '—';
  const d = new Date(ts.seconds * 1000);
  return (
    d.toLocaleDateString('pt-BR') +
    ' às ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  );
}

function formatarDuracao(segundos) {
  if (!segundos || segundos <= 0) return '—';
  const min = Math.floor(segundos / 60);
  const h = Math.floor(min / 60);
  const dias = Math.floor(h / 24);
  if (dias >= 1) {
    const resto = h % 24;
    return resto > 0 ? `${dias}d ${resto}h` : `${dias}d`;
  }
  if (h >= 1) {
    const resto = min % 60;
    return resto > 0 ? `${h}h ${resto}min` : `${h}h`;
  }
  return `${min > 0 ? min : 1}min`;
}

function corDuracao(segundos) {
  if (!segundos || segundos <= 0) return { cor: cores.textoTerciario, fundo: cores.card };
  const dias = segundos / 86400;
  if (dias < 1)  return { cor: cores.sucesso,  fundo: '#E1F5EE' };
  if (dias <= 7) return { cor: cores.alerta,   fundo: '#FCEFDB' };
  return           { cor: cores.erro,     fundo: '#FBE6E6' };
}

// ─── componente ─────────────────────────────────────────────────────────────

export function Historico() {
  const { projetos, carregando: carregandoProjetos } = useProjetos();
  const { historico, carregando: carregandoHistorico } = useHistorico(projetos);

  const [busca, setBusca] = useState('');
  const [filtroProjeto, setFiltroProjeto] = useState('');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroDataDe, setFiltroDataDe] = useState('');
  const [filtroDataAte, setFiltroDataAte] = useState('');

  const usuariosUnicos = useMemo(() => {
    const nomes = new Set(
      historico.map((h) => h.concluidaPorNome || h.responsavel).filter(Boolean)
    );
    return [...nomes].sort();
  }, [historico]);

  const registros = useMemo(() => {
    return historico.filter((item) => {
      if (busca && !(item.nome || '').toLowerCase().includes(busca.toLowerCase())) return false;
      if (filtroProjeto && item.projetoId !== filtroProjeto) return false;
      if (filtroUsuario) {
        const quem = (item.concluidaPorNome || item.responsavel || '').toLowerCase();
        if (!quem.includes(filtroUsuario.toLowerCase())) return false;
      }
      if (filtroDataDe && item.concluidaEm) {
        if (new Date(item.concluidaEm.seconds * 1000) < new Date(filtroDataDe)) return false;
      }
      if (filtroDataAte && item.concluidaEm) {
        const ate = new Date(filtroDataAte);
        ate.setHours(23, 59, 59, 999);
        if (new Date(item.concluidaEm.seconds * 1000) > ate) return false;
      }
      return true;
    });
  }, [historico, busca, filtroProjeto, filtroUsuario, filtroDataDe, filtroDataAte]);

  const temFiltro = busca || filtroProjeto || filtroUsuario || filtroDataDe || filtroDataAte;
  const limparFiltros = () => {
    setBusca('');
    setFiltroProjeto('');
    setFiltroUsuario('');
    setFiltroDataDe('');
    setFiltroDataAte('');
  };

  if (carregandoProjetos || carregandoHistorico) return <Spinner />;

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: cores.texto }}>Histórico</h1>
      </div>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: cores.textoSecundario }}>
        Registro de tarefas concluídas com tempo de execução por colaborador
      </p>

      {/* Resumo */}
      {historico.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 12,
            marginBottom: 24,
          }}
        >
          <ResumoCard
            valor={historico.length}
            rotulo="Tarefas concluídas"
            cor={cores.primario}
            fundo={cores.card}
          />
          <ResumoCard
            valor={new Set(historico.map((h) => h.projetoId)).size}
            rotulo="Projetos com conclusões"
            cor={cores.sucesso}
            fundo="#E1F5EE"
          />
          <ResumoCard
            valor={usuariosUnicos.length}
            rotulo="Colaboradores ativos"
            cor={cores.alerta}
            fundo="#FCEFDB"
          />
        </div>
      )}

      {/* Filtros */}
      <div
        style={{
          backgroundColor: '#fff',
          border: `1px solid ${cores.card}`,
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 12,
          alignItems: 'end',
        }}
      >
        <Campo label="Buscar tarefa">
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome da tarefa..."
          />
        </Campo>

        <Campo label="Projeto">
          <Select value={filtroProjeto} onChange={(e) => setFiltroProjeto(e.target.value)}>
            <option value="">Todos os projetos</option>
            {projetos.map((p) => (
              <option key={p.id} value={p.id}>{p.nome}</option>
            ))}
          </Select>
        </Campo>

        <Campo label="Colaborador">
          <Select value={filtroUsuario} onChange={(e) => setFiltroUsuario(e.target.value)}>
            <option value="">Todos</option>
            {usuariosUnicos.map((nome) => (
              <option key={nome} value={nome}>{nome}</option>
            ))}
          </Select>
        </Campo>

        <Campo label="Data de">
          <Input
            type="date"
            value={filtroDataDe}
            onChange={(e) => setFiltroDataDe(e.target.value)}
          />
        </Campo>

        <Campo label="Data até">
          <Input
            type="date"
            value={filtroDataAte}
            onChange={(e) => setFiltroDataAte(e.target.value)}
          />
        </Campo>

        {temFiltro && (
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={limparFiltros}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 8,
                border: `1px solid ${cores.card}`,
                backgroundColor: 'transparent',
                color: cores.erro,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ✕ Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Contagem dos resultados filtrados */}
      {temFiltro && (
        <p style={{ fontSize: 13, color: cores.textoSecundario, margin: '0 0 12px' }}>
          {registros.length} registro{registros.length !== 1 ? 's' : ''} encontrado{registros.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Lista */}
      {registros.length === 0 ? (
        <EstadoVazio
          titulo={temFiltro ? 'Nenhum resultado para esse filtro' : 'Nenhuma tarefa concluída ainda'}
          descricao={temFiltro ? 'Ajuste os filtros para ver outros registros.' : 'As tarefas concluídas aparecerão aqui com o tempo de execução.'}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {registros.map((item) => {
            const duracaoSeg =
              item.concluidaEm && item.criadoEm
                ? item.concluidaEm.seconds - item.criadoEm.seconds
                : null;
            const { cor: corDur, fundo: fundoDur } = corDuracao(duracaoSeg);
            const quemConcluiu = item.concluidaPorNome || item.responsavel || '—';

            return (
              <div
                key={`${item.projetoId}-${item.etapaId}-${item.id}`}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${cores.card}`,
                  borderLeft: `4px solid ${cores.sucesso}`,
                  borderRadius: 10,
                  padding: '14px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: 20, flexShrink: 0 }}>✅</span>

                <div style={{ flex: 1, minWidth: 160 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: cores.texto }}>
                    {item.nome}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: cores.textoSecundario }}>
                    {item.projetoNome}
                  </p>
                </div>

                {/* Quem concluiu */}
                <div style={{ textAlign: 'center', minWidth: 90 }}>
                  <p style={{ margin: 0, fontSize: 11, color: cores.textoTerciario, textTransform: 'uppercase', letterSpacing: 0.5 }}>Concluído por</p>
                  <p style={{ margin: '3px 0 0', fontSize: 13, fontWeight: 600, color: cores.texto }}>{quemConcluiu}</p>
                </div>

                {/* Datas */}
                <div style={{ textAlign: 'center', minWidth: 130 }}>
                  <p style={{ margin: 0, fontSize: 11, color: cores.textoTerciario, textTransform: 'uppercase', letterSpacing: 0.5 }}>Criada em</p>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: cores.textoSecundario }}>{formatarData(item.criadoEm)}</p>
                </div>

                <div style={{ textAlign: 'center', minWidth: 130 }}>
                  <p style={{ margin: 0, fontSize: 11, color: cores.textoTerciario, textTransform: 'uppercase', letterSpacing: 0.5 }}>Concluída em</p>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: cores.textoSecundario }}>{formatarData(item.concluidaEm)}</p>
                </div>

                {/* Duração */}
                <div
                  style={{
                    textAlign: 'center',
                    backgroundColor: fundoDur,
                    borderRadius: 8,
                    padding: '6px 14px',
                    flexShrink: 0,
                  }}
                >
                  <p style={{ margin: 0, fontSize: 11, color: corDur, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Duração</p>
                  <p style={{ margin: '3px 0 0', fontSize: 15, fontWeight: 700, color: corDur }}>
                    {formatarDuracao(duracaoSeg)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ResumoCard({ valor, rotulo, cor, fundo }) {
  return (
    <div
      style={{
        backgroundColor: fundo,
        borderRadius: 10,
        padding: '14px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span style={{ fontSize: 26, fontWeight: 700, color: cor }}>{valor}</span>
      <span style={{ fontSize: 12, color: cor, fontWeight: 600 }}>{rotulo}</span>
    </div>
  );
}
